import type { NIBFormatType } from '@/types';
import { NIB_CONSTANTS } from '@/types';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

interface FormatTypeSelectorProps {
  value: NIBFormatType;
  onChange: (value: NIBFormatType) => void;
  disabled?: boolean;
}

export const FormatTypeSelector = ({
  value,
  onChange,
  disabled = false,
}: FormatTypeSelectorProps) => {
  const formatOptions: Array<{
    value: NIBFormatType;
    label: string;
    description: string;
    bytes: number;
  }> = [
    {
      value: 'NON_HOSPITALITY',
      label: 'Non-Hospitality',
      description: 'Standard payroll format',
      bytes: NIB_CONSTANTS.NON_HOSPITALITY_RECORD_LENGTH,
    },
    {
      value: 'HOSPITALITY',
      label: 'Hospitality',
      description: 'Includes gratuity contributions',
      bytes: NIB_CONSTANTS.HOSPITALITY_RECORD_LENGTH,
    },
  ];

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Format Type
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {formatOptions.map(option => (
          <label
            key={option.value}
            className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${
                value === option.value
                  ? 'border-primary-600 ring-2 ring-primary-600'
                  : 'border-gray-300'
              }
              ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-gray-400'}
            `}
          >
            <input
              type="radio"
              name="format-type"
              value={option.value}
              checked={value === option.value}
              onChange={e => onChange(e.target.value as NIBFormatType)}
              disabled={disabled}
              className="sr-only"
            />
            <div className="flex flex-1 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="block text-sm font-medium text-gray-900">
                    {option.label}
                  </span>
                  {option.value === 'HOSPITALITY' && (
                    <BuildingOffice2Icon
                      className="h-4 w-4 text-primary-600"
                      aria-hidden="true"
                      title="Hospitality"
                    />
                  )}
                </div>
                <span className="mt-1 flex items-center text-sm text-gray-500">
                  {option.description}
                </span>
                <span className="mt-1 text-xs text-gray-400">
                  {option.bytes} bytes per record
                </span>
              </div>
              <div
                className={`
                  ml-3 flex h-5 w-5 items-center justify-center rounded-full border-2
                  ${
                    value === option.value
                      ? 'border-primary-600'
                      : 'border-gray-300'
                  }
                `}
              >
                {value === option.value && (
                  <div className="h-2.5 w-2.5 rounded-full bg-primary-600" />
                )}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};
